import React, { useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useAttachmentRotationProcessQuery } from 'components/hooks/attachmentRotationProcessQueries';
import { useCreateAttachmentRotationProcessMutation } from 'components/hooks/attachmentsMutation';
import useDebounce from 'components/hooks/useDebounce';

import { AttachmentActions } from 'components/attachments/components/AttachmentActions';
import { AttachmentImage } from 'components/attachments/components/AttachmentImage';
import { useBreadBoard } from 'components/contexts/Toaster';

import { formatData } from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';

export function AttachmentEditor({
  attachmentsQueryKeyUuid,
  coverImage,
  currentAttachment,
  domainAttachment,
  draggable,
  onCaptionSave,
  onDeleteButtonClick,
  onPreview,
  onRotateSuccess,
  onSelectSize,
  originalAttachment,
}) {
  const { addInedibleToast } = useBreadBoard();
  const queryClient = useQueryClient();

  const createAttachmentRotationProcessMutation =
    useCreateAttachmentRotationProcessMutation();

  const [captionInputValue, setCaptionInputValue] = useState(
    currentAttachment.caption,
  );

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'attachments.components.AttachmentEditor',
  });

  const { t: i18nTAttachments } = useTranslation('common', {
    keyPrefix: 'attachments',
  });

  // the current attachment caption is reset if there is an error on caption save
  // the caption input value must also be reset if that occurs

  useEffect(() => {
    if (currentAttachment.caption !== captionInputValue) {
      setCaptionInputValue(currentAttachment.caption);
    }
  }, [currentAttachment.caption]);

  useDebounce(captionInputValue, 250, {
    onChange: (debouncedCaptionValue) =>
      debouncedCaptionValue.currentValue !== currentAttachment.caption &&
      onCaptionSave(
        currentAttachment.hashid,
        debouncedCaptionValue.currentValue,
      ),
  });

  const rotationProcessId =
    createAttachmentRotationProcessMutation.data?.data.id;

  const { data: domainRotationProcess, isError: isDomainRotationProcessError } =
    useAttachmentRotationProcessQuery({ rotationProcessId });

  const pollingKey = ['attachmentPolling', domainAttachment?.attributes.hashid];

  const { data: pollingData } = useQuery({
    enabled: !!(
      domainAttachment && !domainAttachment.attributes.attachedProcessed
    ),
    queryKey: pollingKey,
    queryFn: async () => {
      const response = await axios.get(
        `/attachments/${domainAttachment?.attributes.hashid}`,
        { params: { type: domainAttachment?.attributes.recordType } },
      );
      return response.data;
    },
    select: (attachment) =>
      formatData({ data: attachment, resourceTypes: ['coverImage'] }),
    refetchInterval: (response) =>
      response?.data?.attributes?.attachedProcessed ? false : 1000,
  });

  const handleRotate = () => {
    createAttachmentRotationProcessMutation.mutate(
      { hashid: domainAttachment.attributes.hashid },
      { onError: addInedibleToast },
    );
  };

  const handleRotationProcessReset = () => {
    createAttachmentRotationProcessMutation.reset();

    if (rotationProcessId) {
      queryClient.removeQueries({
        queryKey: ['attachments', 'rotationProcesses', rotationProcessId],
        exact: true,
      });
    }
  };

  const setAttachmentsQueryData = ({ data, included }) => {
    queryClient.setQueryData(
      ['attachments', attachmentsQueryKeyUuid],
      (existingDomainAttachments) =>
        produce(existingDomainAttachments, (draft) => {
          if (!draft) return draft;

          const index = draft.data.findIndex(
            (attachment) =>
              attachment.attributes.hashid ===
              domainAttachment.attributes.hashid,
          );

          if (index !== -1) {
            draft.data[index] = data;

            const coverImageId = data.relationships.coverImage.data?.id;

            if (coverImageId) {
              const coverImage = included.coverImage[coverImageId];

              const coverImageIndex = draft.included.findIndex(
                (inclusion) =>
                  inclusion.id === coverImageId &&
                  inclusion.type === 'coverImage',
              );

              if (coverImageIndex === -1) {
                draft.included.push(coverImage);
              } else {
                draft.included[coverImageIndex] = coverImage;
              }
            }
          }
        }),
    );
  };

  // When processed, update existing cache and then remove current polling data
  if (pollingData?.data?.attributes?.attachedProcessed) {
    const { data, included } = pollingData;
    setAttachmentsQueryData({ data, included });
    queryClient.removeQueries(pollingKey);
  }

  const isDomainRotationProcessCompleted =
    domainRotationProcess?.data?.attributes?.status === 'completed';

  if (isDomainRotationProcessCompleted) {
    onRotateSuccess(domainAttachment.attributes.hashid);

    setAttachmentsQueryData({
      data: domainRotationProcess.included.attachment[
        domainRotationProcess.data.relationships.attachment.data.id
      ],
      included: domainRotationProcess.included,
    });

    handleRotationProcessReset();
  }

  const isDomainRotationProcessFailed =
    isDomainRotationProcessError ||
    domainRotationProcess?.data?.attributes?.status === 'failed';

  if (isDomainRotationProcessFailed) {
    addInedibleToast();
    handleRotationProcessReset();
  }

  const isPdf = domainAttachment?.attributes.recordType === 'pdf';

  const imageSrc =
    isPdf ?
      coverImage?.attributes.attachedUrl
    : domainAttachment?.attributes.attachedUrl ||
      originalAttachment?.attributes.attachedUrl;

  const isEditable = !!(
    onCaptionSave &&
    onDeleteButtonClick &&
    onRotateSuccess &&
    onSelectSize
  );

  const isRotating =
    createAttachmentRotationProcessMutation.isLoading ||
    (!!rotationProcessId &&
      !(isDomainRotationProcessCompleted || isDomainRotationProcessFailed));

  return (
    <div className='tw-h-full tw-w-full tw-rounded-2xl tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-3'>
      <AttachmentImage
        currentAttachment={currentAttachment}
        draggable={draggable}
        imageSrc={imageSrc}
        isPdf={isPdf}
        isRotating={isRotating}
        onPreview={() => onPreview(domainAttachment)}
      />
      <div className='tw-mt-3'>
        {onCaptionSave ?
          <input
            className='tw-outline-noneplaceholder:tw-text-grey-300 tw-mb-4 tw-w-full tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-300 tw-p-2'
            data-element-name='caption'
            name='caption'
            onChange={(e) => setCaptionInputValue(e.target.value)}
            placeholder={i18nTAttachments('addCaption')}
            type='text'
            value={captionInputValue || ''}
          />
        : <div
            className='tw-mb-4 tw-w-full tw-p-2'
            data-element-name='read-only-caption'
          >
            {captionInputValue || i18nT('caption.placeholder.readOnly')}
          </div>
        }

        {isEditable && (
          <AttachmentActions
            attachmentHashId={currentAttachment.hashid}
            attachmentSize={currentAttachment.displaySizeValue}
            isProcessed={!!domainAttachment?.attributes.attachedProcessed}
            isRotating={isRotating}
            onDeleteButtonClick={onDeleteButtonClick}
            onRotate={handleRotate}
            onSelectSize={onSelectSize}
          />
        )}
      </div>
    </div>
  );
}

AttachmentEditor.propTypes = {
  attachmentsQueryKeyUuid: PropTypes.string.isRequired,
  coverImage: resourceShape('coverImage'),
  currentAttachment: PropTypes.shape({
    caption: PropTypes.string,
    displaySizeValue: PropTypes.number,
    hashid: PropTypes.string,
    position: PropTypes.number,
  }).isRequired,
  domainAttachment: resourceShape('attachment'),
  draggable: PropTypes.shape({
    attributes: PropTypes.object.isRequired,
    listeners: PropTypes.object.isRequired,
  }),
  onCaptionSave: PropTypes.func,
  onDeleteButtonClick: PropTypes.func,
  onPreview: PropTypes.func.isRequired,
  onRotateSuccess: PropTypes.func,
  onSelectSize: PropTypes.func,
  originalAttachment: resourceShape('attachment'),
};
